import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

const bg = '#0a0e14';
const panel = '#141e2e';
const lime = '#39ff14';
const text = '#c8d6e5';
const dim = '#5a6a80';
const border = '#1e2d40';
const glow = `0 0 20px rgba(57,255,20,0.3)`;

export default function LandingV8() {
  return (
    <div className="doctif-page" style={{
      fontFamily: 'Lexend, sans-serif',
      background: bg,
      color: text,
      minHeight: '100vh',
    }}>
      <Helmet><title>doctif — Kinetic Dashboard</title></Helmet>

      {/* Scanline overlay */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 50,
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.012) 2px, rgba(255,255,255,0.012) 4px)',
      }} />

      {/* Header */}
      <header className="doctif-shell" style={{ padding: '20px 0 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 26, fontWeight: 700, color: lime }}>doctif</span>
            <span className="v8-pulse" style={{
              width: 8, height: 8, borderRadius: '50%', background: lime,
              boxShadow: `0 0 8px ${lime}`, display: 'inline-block',
            }} />
            <span style={{ fontSize: 10, color: dim, fontWeight: 500 }}>LIVE</span>
          </div>
          <nav className="doctif-chip-row">
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <a key={n} className="doctif-chip" href={`/${n}`} style={{
                background: n === 8 ? lime : 'transparent',
                color: n === 8 ? bg : dim,
                border: `1px solid ${n === 8 ? lime : border}`,
                fontFamily: 'Chakra Petch, sans-serif', fontSize: 11,
              }}>V{n}</a>
            ))}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="doctif-shell" style={{ padding: '48px 0 64px' }}>
        <p style={{
          fontFamily: 'Chakra Petch, sans-serif', fontSize: 11,
          color: lime, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 18,
        }}>⚡ Système de gestion en temps réel</p>
        <h1 style={{
          fontFamily: 'Chakra Petch, sans-serif',
          fontSize: 'clamp(2.2rem, 6vw, 4.5rem)',
          fontWeight: 700, lineHeight: 1.05, marginBottom: 22,
        }}>
          Vitesse<br />
          <span style={{ color: lime, textShadow: `0 0 30px ${lime}44` }}>clinique.</span>
        </h1>
        <p style={{ color: dim, fontSize: 17, lineHeight: 1.7, maxWidth: 500, marginBottom: 30 }}>
          doctif traite chaque donnée en temps réel. Agenda, dossiers, facturation —
          synchronisés en moins d'une seconde.
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <a href="/accounts/login/" style={{
            fontFamily: 'Chakra Petch, sans-serif', fontSize: 14, fontWeight: 700,
            background: lime, color: bg, padding: '13px 24px',
            textDecoration: 'none', borderRadius: 6, boxShadow: glow,
          }}>Lancer doctif</a>
          <a href="/booking" style={{
            fontFamily: 'Chakra Petch, sans-serif', fontSize: 14, fontWeight: 500,
            background: 'transparent', color: text, padding: '13px 24px',
            textDecoration: 'none', borderRadius: 6, border: `1px solid ${border}`,
          }}>Voir la démo</a>
        </div>
      </section>

      {/* Real-time metrics */}
      <div style={{ borderTop: `1px solid ${border}`, borderBottom: `1px solid ${border}` }}>
        <div className="doctif-shell" style={{
          padding: '20px 0', display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)', gap: 16,
        }}>
          {[
            { v: '240+', l: 'CABINETS', bar: 78 },
            { v: '61k', l: 'RDV/MOIS', bar: 92 },
            { v: '34%', l: 'TEMPS GAGNÉ', bar: 34 },
            { v: '<1s', l: 'LATENCE', bar: 99 },
          ].map(m => (
            <div key={m.l}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 24, fontWeight: 700, color: '#fff' }}>{m.v}</span>
                <span style={{ fontSize: 9, color: dim, letterSpacing: '0.15em' }}>{m.l}</span>
              </div>
              <div style={{ height: 3, background: border, borderRadius: 2, overflow: 'hidden' }}>
                <div className="v8-bar-fill" style={{
                  height: '100%', width: `${m.bar}%`,
                  background: `linear-gradient(90deg, ${lime}, ${lime}88)`,
                  borderRadius: 2, boxShadow: `0 0 8px ${lime}44`,
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <section className="doctif-shell" style={{ padding: '60px 0 48px' }}>
        <p style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 11, color: lime, letterSpacing: '0.2em', marginBottom: 12 }}>MODULES</p>
        <h2 style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 'clamp(1.4rem, 3.5vw, 2.2rem)', fontWeight: 700, marginBottom: 40, color: '#fff' }}>
          Quatre moteurs, <span style={{ color: lime }}>zéro friction.</span>
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
          {[
            { t: 'Agenda', d: 'Optimisation temps-réel des créneaux. Confirmations instantanées.', p: '99.7%', pl: 'uptime' },
            { t: 'Dossiers', d: "Accès < 200ms. Recherche full-text sur l'ensemble des données.", p: '< 200ms', pl: 'access' },
            { t: 'Facturation', d: "Pipeline automatisé. Détection d'anomalies intégrée.", p: '0 erreur', pl: 'billing' },
            { t: 'Multi-site', d: 'Architecture distribuée. Isolation totale des données.', p: '100%', pl: 'isolation' },
          ].map(f => (
            <article key={f.t} style={{
              background: panel, border: `1px solid ${border}`, borderRadius: 10,
              padding: '22px 18px', transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = lime; e.currentTarget.style.boxShadow = `0 0 20px ${lime}15`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 10 }}>
                <h3 style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 17, fontWeight: 700, color: '#fff' }}>{f.t}</h3>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 16, fontWeight: 700, color: lime }}>{f.p}</div>
                  <div style={{ fontSize: 9, color: dim, letterSpacing: '0.1em' }}>{f.pl}</div>
                </div>
              </div>
              <p style={{ color: dim, fontSize: 13, lineHeight: 1.6 }}>{f.d}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Dashboard — Activity chart */}
      <section className="doctif-shell" style={{ paddingBottom: 60 }}>
        <div style={{
          background: panel, border: `1px solid ${border}`, borderRadius: 14,
          padding: 20,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span className="v8-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: lime, boxShadow: `0 0 6px ${lime}`, display: 'inline-block' }} />
              <span style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 12, color: dim }}>FLUX EN DIRECT</span>
            </div>
            <span style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 12, color: dim }}>25.03.2026</span>
          </div>
          {/* Activity bars */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4, marginBottom: 12 }}>
            {[35,52,78,45,90,100,88,65,42,30,18,8].map((h, i) => (
              <div key={i} style={{ height: 60, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{
                  height: `${h}%`,
                  background: `linear-gradient(180deg, ${lime}, ${lime}44)`,
                  borderRadius: '3px 3px 0 0',
                  boxShadow: h > 80 ? `0 0 8px ${lime}33` : 'none',
                }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {['08h','09h','10h','11h','12h','13h','14h','15h','16h','17h','18h','19h'].map(t => (
              <span key={t} style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 9, color: dim }}>{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section style={{ borderTop: `1px solid ${border}`, borderBottom: `1px solid ${border}` }}>
        <div className="doctif-shell" style={{ padding: '56px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <p style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 11, color: lime, letterSpacing: '0.2em', marginBottom: 10 }}>PIPELINE</p>
            <h2 style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 'clamp(1.4rem, 3.5vw, 2.2rem)', fontWeight: 700, color: '#fff' }}>
              Flux <span style={{ color: lime }}>continu.</span>
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[
              { s: '01', t: 'Input', d: 'Patient réserve en ligne → validation automatique → confirmation push.', i: '📡' },
              { s: '02', t: 'Process', d: 'Consultation au fauteuil → capture données temps réel → codification.', i: '⚙️' },
              { s: '03', t: 'Output', d: 'Facture générée → envoi automatique → analytics mis à jour.', i: '📊' },
            ].map(w => (
              <div key={w.s} style={{
                background: panel, border: `1px solid ${border}`, borderRadius: 10,
                padding: '24px 18px', textAlign: 'center',
                transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = lime)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = border)}
              >
                <div style={{ fontSize: 28, marginBottom: 10 }}>{w.i}</div>
                <div style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 10, color: lime, letterSpacing: '0.15em', marginBottom: 6 }}>STEP_{w.s}</div>
                <h3 style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 8 }}>{w.t}</h3>
                <p style={{ color: dim, fontSize: 12, lineHeight: 1.6 }}>{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="doctif-shell" style={{ padding: '56px 0 48px' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <p style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 11, color: lime, letterSpacing: '0.2em', marginBottom: 10 }}>FEEDBACK</p>
          <h2 style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 'clamp(1.2rem, 3vw, 1.8rem)', fontWeight: 700, color: '#fff' }}>
            Performance <span style={{ color: lime }}>validée.</span>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            { q: "Synchronisation instantanée entre sites. On gagne 40% de temps administratif.", n: 'Dr. Khalil', cl: 'Al Madina', p: '40%↑' },
            { q: "Le dashboard temps réel a transformé notre pilotage. Données toujours fraîches.", n: 'Dr. Brahmi', cl: 'Corbei', p: '< 1s' },
          ].map(t => (
            <div key={t.n} style={{
              background: panel, border: `1px solid ${border}`, borderRadius: 10,
              padding: '22px 18px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ color: '#fbbf24', fontSize: 14, letterSpacing: 2 }}>★★★★★</div>
                <span style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 14, fontWeight: 700, color: lime }}>{t.p}</span>
              </div>
              <blockquote style={{ fontSize: 15, lineHeight: 1.5, margin: '0 0 16px', color: text }}>"{t.q}"</blockquote>
              <div style={{ fontSize: 12, color: dim }}>
                <span style={{ fontWeight: 700, color: text }}>{t.n}</span> — {t.cl}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="doctif-shell" style={{ padding: '56px 0', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'Chakra Petch, sans-serif',
          fontSize: 'clamp(1.6rem, 4.5vw, 2.8rem)',
          fontWeight: 700, color: '#fff', marginBottom: 12,
        }}>
          Prêt à <span style={{ color: lime, textShadow: `0 0 20px ${lime}44` }}>accélérer ?</span>
        </h2>
        <p style={{ color: dim, maxWidth: 400, margin: '0 auto 24px', fontSize: 14, lineHeight: 1.6 }}>
          Rejoignez le réseau haute performance. Sans engagement.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <a href="/accounts/login/" style={{
            fontFamily: 'Chakra Petch, sans-serif', fontSize: 14, fontWeight: 700,
            background: lime, color: bg, padding: '14px 28px',
            textDecoration: 'none', borderRadius: 6, boxShadow: glow,
          }}>Lancer doctif →</a>
          <a href="/booking" style={{
            fontFamily: 'Chakra Petch, sans-serif', fontSize: 14,
            background: 'transparent', color: text, padding: '14px 24px',
            textDecoration: 'none', borderRadius: 6, border: `1px solid ${border}`,
          }}>Voir la démo</a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${border}` }}>
        <div className="doctif-shell" style={{ padding: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'Chakra Petch, sans-serif', fontSize: 16, color: dim }}>doctif</span>
            <span className="v8-pulse" style={{ width: 5, height: 5, borderRadius: '50%', background: lime, boxShadow: `0 0 4px ${lime}`, display: 'inline-block' }} />
            <span style={{ fontSize: 10, color: dim }}>v8.0 — Kinetic Dashboard</span>
          </div>
          <div style={{ display: 'flex', gap: 18 }}>
            {['Privacy', 'Terms', 'Contact'].map(l => (
              <a key={l} href="#" style={{ color: dim, fontSize: 11, textDecoration: 'none', fontFamily: 'Chakra Petch, sans-serif' }}>{l}</a>
            ))}
          </div>
        </div>
      </footer>

      <style>{`
        .v8-pulse { animation: v8p 2s ease-in-out infinite; }
        @keyframes v8p { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
      `}</style>
    </div>
  );
}
